import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import PRESET_AVATARS from '../utils/presetAvatars';
import '../styles/Login.css';

const CodeRegister = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get('code') || '';
  const [step, setStep] = useState(initialCode ? 'details' : 'code');
  const [code, setCode] = useState(initialCode);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a registration code');
      return;
    }
    if (!trimmed.startsWith('REG-')) {
      setError('Code must start with REG-');
      return;
    }
    setCode(trimmed);
    setError('');
    setStep('details');
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Fall back to DOM values if React state is empty (browser autofill)
    const uEl = document.getElementById('reg-username');
    const pEl = document.getElementById('reg-password');
    const finalUsername = username || (uEl && uEl.value) || '';
    const finalPassword = password || (pEl && pEl.value) || '';
    if (finalUsername) setUsername(finalUsername);
    if (finalPassword) setPassword(finalPassword);

    if (!finalUsername.trim()) {
      setError('Please enter a username');
      return;
    }
    if (finalPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/register-with-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, username: finalUsername.trim(), password: finalPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      localStorage.setItem('userId', data.userId);
      localStorage.setItem('token', data.token);
      localStorage.setItem('username', data.username);

      if (onLogin) {
        onLogin(data.userId, data.token);
      }

      setRegisteredUserId(data.userId);

      // If photo was already selected during details step, upload it and go to market
      if (profilePicPreview) {
        try {
          await fetch(`${API_BASE_URL}/api/profile/picture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: data.userId, profilePicture: profilePicPreview })
          });
        } catch (_) {
          // Photo upload failed silently — not critical
        }
        navigate('/market');
        return;
      }

      setStep('photo');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Photo must be less than 10MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }
    setLoadingPhoto(true);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Resize to max 400px to keep upload fast
      const img = new Image();
      img.onload = () => {
        const max = 400;
        let w = img.width, h = img.height;
        if (w > max || h > max) {
          if (w > h) { h = Math.round(h * max / w); w = max; }
          else { w = Math.round(w * max / h); h = max; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        setProfilePicPreview(canvas.toDataURL('image/jpeg', 0.85));
        setLoadingPhoto(false);
      };
      img.onerror = () => {
        setError('Failed to load image');
        setLoadingPhoto(false);
      };
      img.src = ev.target.result;
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setLoadingPhoto(false);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async () => {
    if (!profilePicPreview || !registeredUserId) return;
    setUploadingPic(true);
    setError('');
    try {
      await fetch(`${API_BASE_URL}/api/profile/picture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: registeredUserId, profilePicture: profilePicPreview })
      });
      navigate('/market');
    } catch (err) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPic(false);
    }
  };

  return (
    <div className="login-terminal">
      <div className="terminal-scanlines"></div>

      <div className="terminal-header">
        <div className="terminal-logo">KOMOK</div>
        <div className="terminal-subtitle">NEW EMPLOYEE REGISTRATION</div>
      </div>

      <div className="terminal-window">
        <div className="terminal-title-bar">
          <span className="terminal-title">
            {step === 'code' ? 'ENTER REGISTRATION CODE' : step === 'photo' ? 'ADD YOUR BADGE PHOTO' : 'CREATE YOUR ACCOUNT'}
          </span>
          <div className="terminal-dots">
            <span className="dot red"></span>
            <span className="dot yellow"></span>
            <span className="dot green"></span>
          </div>
        </div>

        <div className="terminal-content">
          {error && (
            <div className="terminal-error">
              <span className="error-icon">!</span>
              <span className="error-text">{error}</span>
              <button onClick={() => setError('')} className="error-dismiss">x</button>
            </div>
          )}

          {step === 'photo' && (
            <div className="login-form" style={{ textAlign: 'center' }}>
              <div style={{ color: '#3fb950', fontSize: '14px', marginBottom: '16px' }}>
                Welcome, @{username}!
              </div>
              <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '16px' }}>
                Add a profile photo for your badge (optional)
              </div>

              {loadingPhoto ? (
                <div style={{
                  width: '120px', height: '120px', borderRadius: '50%',
                  border: '2px solid #3fb950', margin: '0 auto 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#3fb950', fontSize: '12px', fontFamily: 'monospace',
                }}>
                  <span className="loading-spinner" style={{ marginRight: '6px' }}></span>
                  Processing...
                </div>
              ) : profilePicPreview ? (
                <div style={{ marginBottom: '16px' }}>
                  <img
                    src={profilePicPreview}
                    alt="Preview"
                    style={{
                      width: '120px', height: '120px', borderRadius: '50%',
                      objectFit: 'cover', border: '2px solid #3fb950',
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  width: '120px', height: '120px', borderRadius: '50%',
                  border: '2px dashed #444', margin: '0 auto 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#555', fontSize: '36px',
                }}>?</div>
              )}

              <label style={{
                display: 'inline-block', padding: '10px 20px',
                cursor: loadingPhoto || uploadingPic ? 'not-allowed' : 'pointer',
                background: 'transparent', border: '1px solid #3fb950', color: '#3fb950',
                borderRadius: '6px', fontFamily: 'inherit', fontSize: '13px',
                marginBottom: '12px',
                opacity: loadingPhoto || uploadingPic ? 0.4 : 1,
              }}>
                {loadingPhoto ? 'PROCESSING...' : profilePicPreview ? 'CHANGE PHOTO' : 'CHOOSE PHOTO'}
                <input type="file" accept="image/*" onChange={handlePhotoSelect}
                  disabled={loadingPhoto || uploadingPic}
                  style={{ display: 'none' }} />
              </label>

              <div style={{ color: '#8b949e', fontSize: '11px', fontFamily: 'monospace', margin: '10px 0 6px' }}>
                OR PICK A PRESET
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px',
                maxWidth: '280px', margin: '0 auto 16px',
              }}>
                {PRESET_AVATARS.map((avatar, i) => (
                  <img
                    key={i}
                    src={avatar}
                    alt={`Preset ${i + 1}`}
                    onClick={() => setProfilePicPreview(avatar)}
                    style={{
                      width: '100%', aspectRatio: '1', borderRadius: '50%',
                      border: profilePicPreview === avatar ? '2px solid #3fb950' : '2px solid #444',
                      cursor: 'pointer', objectFit: 'cover', background: '#fff',
                      boxShadow: profilePicPreview === avatar ? '0 0 6px rgba(63,185,80,0.5)' : 'none',
                    }}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                {profilePicPreview && (
                  <button
                    className="login-button"
                    onClick={handlePhotoUpload}
                    disabled={uploadingPic}
                    style={{ flex: 1 }}
                  >
                    {uploadingPic ? 'UPLOADING...' : 'SAVE & CONTINUE'}
                  </button>
                )}
                <button
                  className="register-link"
                  onClick={() => navigate('/market')}
                  disabled={uploadingPic}
                  style={{ padding: '10px 20px' }}
                >
                  {profilePicPreview ? 'SKIP' : 'SKIP FOR NOW'}
                </button>
              </div>
            </div>
          )}

          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className="login-form">
              <div className="input-group">
                <label htmlFor="reg-code">
                  <span className="label-icon">{'>'}</span>
                  REGISTRATION CODE
                </label>
                <input
                  type="text"
                  id="reg-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. REG-BOXPFH6R"
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <button type="submit" className="login-button">
                VERIFY CODE
              </button>
            </form>
          )}

          {step === 'details' && (
            <form onSubmit={handleRegister} className="login-form">
              <div style={{ textAlign: 'center', marginBottom: '12px', color: '#3fb950', fontSize: '12px', fontFamily: 'monospace' }}>
                CODE: {code}
              </div>

              <div className="input-group">
                <label htmlFor="reg-username">
                  <span className="label-icon">{'>'}</span>
                  USERNAME
                </label>
                <input
                  type="text"
                  id="reg-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  maxLength="20"
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <div className="input-group">
                <label htmlFor="reg-password">
                  <span className="label-icon">{'>'}</span>
                  PASSWORD
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="reg-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 4 characters"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? '\u25C9' : '\u25CB'}
                  </button>
                </div>
              </div>

              {/* Profile picture (optional) */}
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <div style={{ color: '#8b949e', fontSize: '11px', fontFamily: 'monospace', marginBottom: '8px' }}>
                  BADGE PHOTO (OPTIONAL)
                </div>
                {loadingPhoto ? (
                  <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    border: '2px solid #3fb950', margin: '0 auto 8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#3fb950', fontSize: '11px', fontFamily: 'monospace',
                  }}>
                    <span className="loading-spinner" style={{ marginRight: '4px' }}></span>
                    ...
                  </div>
                ) : profilePicPreview ? (
                  <div style={{ marginBottom: '8px' }}>
                    <img
                      src={profilePicPreview}
                      alt="Preview"
                      style={{
                        width: '80px', height: '80px', borderRadius: '50%',
                        objectFit: 'cover', border: '2px solid #3fb950',
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    border: '2px dashed #444', margin: '0 auto 8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#555', fontSize: '24px',
                  }}>?</div>
                )}
                <label style={{
                  display: 'inline-block', padding: '6px 14px',
                  cursor: loadingPhoto ? 'not-allowed' : 'pointer',
                  background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
                  borderRadius: '4px', fontFamily: 'inherit', fontSize: '11px',
                  opacity: loadingPhoto ? 0.4 : 1,
                }}>
                  {loadingPhoto ? 'PROCESSING...' : profilePicPreview ? 'CHANGE' : 'UPLOAD PHOTO'}
                  <input type="file" accept="image/*" onChange={handlePhotoSelect}
                    disabled={loadingPhoto}
                    style={{ display: 'none' }} />
                </label>
                <div style={{ color: '#8b949e', fontSize: '10px', fontFamily: 'monospace', margin: '10px 0 6px' }}>
                  OR PICK A PRESET
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '5px',
                  maxWidth: '240px', margin: '0 auto',
                }}>
                  {PRESET_AVATARS.map((avatar, i) => (
                    <img
                      key={i}
                      src={avatar}
                      alt={`Preset ${i + 1}`}
                      onClick={() => setProfilePicPreview(avatar)}
                      style={{
                        width: '100%', aspectRatio: '1', borderRadius: '50%',
                        border: profilePicPreview === avatar ? '2px solid #3fb950' : '2px solid #333',
                        cursor: 'pointer', objectFit: 'cover', background: '#fff',
                        boxShadow: profilePicPreview === avatar ? '0 0 6px rgba(63,185,80,0.5)' : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{
                fontSize: '10px',
                color: '#6e7681',
                fontFamily: 'monospace',
                textAlign: 'center',
                marginBottom: '12px',
                lineHeight: '1.4',
              }}>
                By registering you agree to our{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  style={{ color: '#3fb950', textDecoration: 'underline' }}
                >
                  Terms, Conditions & Crises
                </Link>
              </div>

              <button
                type="submit"
                className={`login-button ${loading ? 'loading' : ''}`}
                disabled={loading || loadingPhoto}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    REGISTERING...
                  </>
                ) : (
                  'REGISTER'
                )}
              </button>

              <button
                type="button"
                className="register-link"
                onClick={() => { setStep('code'); setError(''); }}
                style={{ marginTop: '8px' }}
              >
                {'<'} BACK TO CODE
              </button>
            </form>
          )}

          <div className="terminal-footer">
            <div className="footer-line"></div>
            <p className="footer-text">
              Already have an account?
            </p>
            <button
              className="register-link"
              onClick={() => navigate('/login')}
            >
              LOGIN
            </button>
          </div>
        </div>
      </div>

      <div className="terminal-tagline">
        "Your productivity is our profit"
      </div>
    </div>
  );
};

export default CodeRegister;
